import { For, If, reactive, watch } from 'cdui-js';
import { isChinaConnectOption } from '../infomations/SelectAccount';
import { editProcess, lastProcess, nextProcess, normalizeAddressType, pState } from '../../store';
import { busConfigs, busConfigsMaps, busConfigsTick } from '../../../../../../stores/bus-config';
import { LastNextBtns } from '../../components/LastNextBtns';
import i18n from '../../../../../../i18n';
import { OTHER_CODE, SOURCE_OF_FUNDS_LOCATION_OTHER } from '../infomations/financeOptions';
import { isMainlandIdCard, stepOf } from '../stepMaps';
import { isNoneExperienceType, resolveExperienceYearField } from '../infomations/investmentExperienceMap';
import { getTaxCountryCode } from '../infomations/taxSync';
import { namePartDisplayValues } from '../../../nameParts';

const EMPLOYMENT_RETIRED = 'RETIRED';
const EMPLOYMENT_UNEMPLOYED = 'UNEMPLOYED';
const DICT_OTHER = 'OTHER';

/** 是/否 + 可选补充说明（逗号分隔，无括号） */
const formatYesNoDesc = (flag: number, desc?: string) => {
  if (flag === 1) {
    const detail = (desc || '').trim();
    return detail ? `${i18n.OpenProgress.Common.Yes}，${detail}` : i18n.OpenProgress.Common.Yes;
  }
  if (flag === 0) return i18n.OpenProgress.Common.No;
  return '-';
};

/** 双字段：是，字段1 【8px】字段2（同一行） */
const formatYesNoPairValue = (flag: number, part1: string, part2: string) => {
  if (flag === 0) return i18n.OpenProgress.Common.No;
  if (flag !== 1) return '-';
  const yes = i18n.OpenProgress.Common.Yes;
  const p1 = (part1 || '').trim();
  const p2 = (part2 || '').trim();
  if (p1 && p2) {
    return (
      <>
        {`${yes}，${p1}`}
        <span style={{ 'margin-left': '8px' }}>{p2}</span>
      </>
    );
  }
  if (p1) return `${yes}，${p1}`;
  if (p2) return `${yes}，${p2}`;
  return yes;
};

interface ValuesItem {
  label: string;
  value: number | string | Function;
}

interface ListItem {
  label: string;
  values: ValuesItem[];
  editPage: number;
  /** 跨页修改时的区间结束步骤；不填则为单页修改 */
  editEndPage?: number;
  /** 不展示「修改」入口 */
  hideEdit?: boolean;
  /** 字段上下排列（如其他资料披露） */
  stackLayout?: boolean;
}

export const ConfirmInfos = () => {
  const state = reactive({
    list: [] as ListItem[],
  });
  const C = i18n.OpenProgress.Risk.Confirm;

  /** 字典晚到（App Theme / 接口）后重建回显文案 */
  const rebuildList = () => {
  const data = pState;
  const lic = data.uploadLicForm;

  const address = data.addressForm;
  const job = data.jobStatusForm;
  const finance = data.financialStatusForm;
  const investmentExp = data.investmentExpForm;
  const tax = data.taxInfoForm;
  const others = data.othersForm;
  const account = data.selectAccountForm;
  const J = i18n.OpenProgress.Info.Job;
  const I = i18n.OpenProgress.Info.Investment;
  const U = i18n.OpenProgress.Info.UploadLic;
  const O = i18n.OpenProgress.Info.Others;
  const isMainland = isMainlandIdCard(lic.idCardType);
  const skipCompanyName = job.employmentStatus === EMPLOYMENT_RETIRED || job.employmentStatus === EMPLOYMENT_UNEMPLOYED;
  const checkedTradePermissions = (account.tradePermissionList || []).filter(
    (i) => i.check && !isChinaConnectOption(i),
  );
  /** 仅「有意买卖衍生产品」时展示账户区「衍生产品权限」回显 */
  const hasDerivativePermission = investmentExp.intendTradeDerivative === '1';
  const names = namePartDisplayValues(lic);

  state.list = [
    {
      label: C.SectionId,
      values: [
        { label: U.LabelFullName, value: names.fullName },
        { label: U.LabelFirstNameEn, value: names.firstNameEn },
        { label: U.LabelLastNameEn, value: names.lastNameEn },
        { label: C.FieldGender, value: busConfigsMaps.gender[lic.gender] || '-' },
        { label: C.FieldIdType, value: busConfigsMaps.identityType[lic.idCardType] || '-' },
        { label: C.FieldIdNo, value: lic.idCardNo || '-' },
        { label: U.LabelIssueAuthority, value: lic.idCardIssueAuthority || '-' },
        {
          label: C.FieldIdValidity,
          value: lic.idCardLongTerm
            ? i18n.OpenProgress.Common.Permanent
            : `${lic.idCardStartDate} - ${lic.idCardEndDate}`,
        },
        { label: C.FieldBirthDate, value: lic.birthDate || '-' },
        { label: U.LabelNationality, value: busConfigsMaps.countryList[lic.nationality] || '-' },
        { label: U.LabelBirthCountry, value: busConfigsMaps.countryList[lic.birthCountry] || '-' },
      ],
      // 确认页可回改中文名字、英文名字、英文姓氏等证件字段
      editPage: stepOf('UploadLic', isMainland),
    },
    {
      label: C.SectionAddress,
      values: [
        { label: C.FieldResCountry, value: busConfigsMaps.countryList[address.country] || '-' },
        { label: C.FieldResAddress, value: address.address || '-' },
        ...(String(address.addressCn || '').trim()
          ? [{ label: C.FieldResAddressCn, value: address.addressCn }]
          : []),
        {
          label: C.FieldMailingAddress,
          value: () => {
            const A = i18n.OpenProgress.Info.Address;
            const labelMap: Record<string, string> = {
              RESIDENTIAL: A.OptResidential,
              BUSINESS: A.OptBusiness,
              OTHER: A.OptOther,
            };
            const code = normalizeAddressType(address.addressType);
            const detail = String(address.correspondenceAddress || address.addressTypeOther || '').trim();
            if (!code) return detail || '-';
            const label = labelMap[code] || code;
            return detail ? `${label}（${detail}）` : label;
          },
        },
        {
          label: C.FieldHomePhone,
          value: [address.phoneCountry, address.phone].filter(Boolean).join(' ') || '-',
        },
        { label: C.FieldEmail, value: address.email || '-' },
      ],
      editPage: stepOf('Address', isMainland),
    },
    {
      label: C.SectionJob,
      values: [
        { label: C.FieldEducation, value: busConfigsMaps.education[job.education] || '-' },
        { label: C.FieldEmployment, value: busConfigsMaps.employmentStatus[job.employmentStatus] || '-' },
        ...(skipCompanyName ? [] : [{ label: J.LabelCompanyName, value: job.companyName || '-' }]),
        {
          label: J.LabelIndustry,
          value: () => {
            const label = busConfigsMaps.industry[job.industry] || '-';
            if (job.industry === DICT_OTHER && job.industryOther) {
              return `${label}（${job.industryOther}）`;
            }
            return label;
          },
        },
        {
          label: J.LabelPosition,
          value: () => {
            const label = busConfigsMaps.position[job.position] || '-';
            if (job.position === DICT_OTHER && job.positionOther) {
              return `${label}（${job.positionOther}）`;
            }
            return label;
          },
        },
        { label: J.LabelCompanyAddress, value: job.companyAddress || '-' },
        { label: J.LabelEmploymentYears, value: busConfigsMaps.employmentYear[job.employmentYears] || '-' },
        { label: J.LabelCompanyPhone, value: job.companyPhone || '-' },
      ],
      editPage: stepOf('JobStatus', isMainland),
    },

    {
      label: C.SectionFinance,
      values: [
        {
          label: C.FieldPropertyStatus,
          value: busConfigsMaps.housingProperty[finance.housingProperty] || '-',
        },
        {
          label: C.FieldAnnualIncome,
          value: busConfigsMaps.annualTotalIncome[finance.annualIncome] || '-',
        },
        {
          label: C.FieldLiquidAssets,
          value: busConfigsMaps.totalAssetNetWorth[finance.totalAssetsNetHkd] || '-',
        },
        {
          label: C.FieldWealthSource,
          value: () => {
            const arr = (finance.wealthSource || '').split(',').filter(Boolean);
            if (!arr.length) return '-';
            return arr.map((code) => {
              const label = busConfigsMaps.sourceOfFunds[code] || code;
              if (code === OTHER_CODE && finance.wealthSourceOther) {
                return (
                  <div>
                    {label}（{finance.wealthSourceOther}）
                  </div>
                );
              }
              return <div>{label}</div>;
            });
          },
        },
        {
          label: C.FieldFundSource,
          value: () => {
            const arr = (finance.sourceOfFunds || '').split(',').filter(Boolean);
            if (!arr.length) return '-';
            return arr.map((code) => {
              const label = busConfigsMaps.sourceOfFunds[code] || code;
              if (code === OTHER_CODE && finance.sourceOfFundsOther) {
                return (
                  <div>
                    {label}（{finance.sourceOfFundsOther}）
                  </div>
                );
              }
              return <div>{label}</div>;
            });
          },
        },
        {
          label: C.FieldFundTransfer,
          value: () => {
            const arr = (finance.fundTransferMethod || '').split(',').filter(Boolean);
            if (!arr.length) return '-';
            return arr.map((code) => (
              <div>{busConfigsMaps.fundTransferMethod[code] || code}</div>
            ));
          },
        },
        {
          label: C.FieldFundRegion,
          value: () => {
            const code = finance.sourceOfFundsLocation || '';
            if (!code) return '-';
            const label = busConfigsMaps.sourceOfFundsLocation[code] || code;
            if (code === SOURCE_OF_FUNDS_LOCATION_OTHER && finance.sourceOfFundsLocationOther) {
              return `${label}（${finance.sourceOfFundsLocationOther}）`;
            }
            return label;
          },
        },
      ],
      editPage: stepOf('FinancialStatus', isMainland),
    },
    {
      label: C.SectionInvestment,
      values: [
        {
          label: C.FieldInvestKnowledge,
          value: busConfigsMaps.investmentKnowledge[investmentExp.investmentKnowledge] || '-',
        },
        {
          label: C.FieldInvestExperienceTypes,
          value: () => {
            const codes = (investmentExp.investmentExperienceTypes || '').split(',').filter(Boolean);
            if (!codes.length) return '-';
            return codes.map((code) => {
              const typeLabel = busConfigsMaps.investmentExperienceType[code] || code;
              const opt = (busConfigs.investmentExperienceType || []).find((o) => String(o.value) === code);
              if (isNoneExperienceType(code, opt?.label)) {
                return <div>{typeLabel}</div>;
              }
              const yearField = resolveExperienceYearField(code, opt?.label);
              const yearLabel = yearField
                ? busConfigsMaps.investmentExperience[investmentExp[yearField]] || investmentExp[yearField] || ''
                : '';
              return (
                <div>
                  {typeLabel}
                  {yearLabel ? `：${yearLabel}` : ''}
                </div>
              );
            });
          },
        },
        {
          label: C.FieldInvestTarget,
          value: () => {
            const arr = (investmentExp.investmentTarget || '').split(',').filter(Boolean);
            if (!arr.length) return '-';
            return arr.map((i) => {
              const label = busConfigsMaps.investmentObjective[i] || i;
              if (i === 'INVESTMENT_PURPOSE_OTHER' && investmentExp.investmentPurposeOther) {
                return (
                  <div>
                    {label}（{investmentExp.investmentPurposeOther}）
                  </div>
                );
              }
              return <div>{label}</div>;
            });
          },
        },
        {
          label: I.IntendTradeDerivative,
          value:
            investmentExp.intendTradeDerivative === '1'
              ? I.IntendTradeYes
              : investmentExp.intendTradeDerivative === '0'
                ? I.IntendTradeNo
                : '-',
        },
      ],
      editPage: stepOf('InvestmentExp', isMainland),
    },
    {
      label: C.SectionTax,
      values: [
        {
          label: C.FieldTaxCountry,
          // 与证件一致：展示按当前证件推导，避免改证件后仍显示旧税务国家
          value: busConfigsMaps.countryList[getTaxCountryCode()] || busConfigsMaps.countryList[tax.country] || '-',
        },
        {
          label: C.FieldTaxNo,
          value: lic.idCardNo || tax.taxNumber || '-',
        },
      ],
      editPage: stepOf('TaxInfo', isMainland),
    },
    {
      label: O.Title,
      values: [
        {
          label: O.SfcQuestion,
          value: () => formatYesNoDesc(others.isSfcLicensed, others.isSfcLicensed === 1 ? others.sfcLicenseDesc : ''),
        },
        {
          label: O.StaffRelativeQuestion,
          value: () =>
            formatYesNoDesc(others.isRelativeToStaff, others.isRelativeToStaff === 1 ? others.staffRelativeDesc : ''),
        },
        {
          label: O.SpouseAccountQuestion,
          value: () =>
            formatYesNoPairValue(
              others.isSpouseHoldCompanyAccount,
              others.spouseHoldName,
              others.spouseHoldAccountNo,
            ),
        },
        {
          label: O.VotingControlQuestion,
          value: () =>
            formatYesNoPairValue(
              others.isControlOtherClientVoting35,
              others.controlVotingAccountName,
              others.controlVotingAccountNo,
            ),
        },
      ],
      editPage: stepOf('Others', isMainland),
      stackLayout: true,
    },
    {
      label: C.SectionAccount,
      values: [
        {
          label: C.FieldSecurities,
          value: () => {
            const arr = account.securitiesAccountType.split(',');
            return arr.map((i) => <div>{busConfigsMaps.securitiesAccountType[i]}</div>);
          },
        },
        // 未选衍生品时不展示该行（避免回显「否」）；A股通不回显
        ...(hasDerivativePermission
          ? [
              {
                label: C.FieldTradePermission,
                value: () => {
                  if (checkedTradePermissions.length) {
                    return checkedTradePermissions.map((i) => (
                      <div>{i.label || busConfigsMaps.tradePermission[i.value] || i.value}</div>
                    ));
                  }
                  const dict = (busConfigs.tradePermission || []).filter((i) => !isChinaConnectOption(i));
                  if (dict.length) {
                    return dict.map((i) => <div>{i.label}</div>);
                  }
                  return i18n.OpenProgress.Common.Yes;
                },
              },
            ]
          : []),
        // {
        //   label: C.FieldFundAccount,
        //   value: () => {
        //     const arr = account.fundAccountType.split(',');
        //     return arr.map((i) => <div>{busConfigsMaps.fundAccountType[i]}</div>);
        //   },
        // },
      ],
      editPage: stepOf('SelectAccount', isMainland),
    },
  ];
  };

  rebuildList();
  watch(
    () => busConfigsTick.v,
    () => {
      rebuildList();
    },
  );

  return (
    <div class="margin-y-XL">
      <div class="margin-b-L font-s-L font-bold color-D padding-x-L">{C.Title}</div>

      <div style={{ display: 'flex', 'flex-direction': 'column', gap: '20px' }}>
        <For each={state.list}>
          {(item) => (
            <div>
              <div class="flex justify-space-between padding-m-x padding-y-S">
                <div class="font-bold color-D font-s">{item.label}</div>
                <If when={!item.hideEdit}>
                  <div
                    class="link-color"
                    onclick={() => {
                      editProcess(item.editPage, item.editEndPage);
                    }}
                  >
                    {i18n.OpenProgress.Common.Edit}
                  </div>
                </If>
              </div>

              <div class="padding-x-L">
                <For each={item.values}>
                  {(citem) =>
                    item.stackLayout ? (
                      <div class="confirm-infos-stack-item padding-y-S">
                        <div class="color-L font-s-S confirm-infos-stack-label">{citem.label}</div>
                        <div class="color-D font-s-S margin-t-XS confirm-infos-value">
                          {typeof citem.value === 'function' ? citem.value() : citem.value}
                        </div>
                      </div>
                    ) : (
                      <div class="flex padding-y-XS" style={{ 'min-width': 0 }}>
                        <div class="color-L font-s-S" style={{ flex: 2, 'min-width': 0 }}>
                          {citem.label}
                        </div>
                        <div class="color-D font-s-S confirm-infos-value" style={{ flex: 3, 'min-width': 0 }}>
                          {typeof citem.value === 'function' ? citem.value() : citem.value}
                        </div>
                      </div>
                    )
                  }
                </For>
              </div>
            </div>
          )}
        </For>
      </div>

      <LastNextBtns
        last={() => {
          lastProcess();
        }}
        next={() => {
          nextProcess();
        }}
      />
    </div>
  );
};
